import { Show } from "shared/components";
import { formatPhone } from "shared/utils";
import { LinkifiedText } from "shared/utils/linkify-text";
import { MessageContentCard } from "./MessageContentCard";
import { MessageAttachments } from "./MessageAttachments";
import { MessageTranslateButton } from "./MessageTranslateButton";
import type { MessageTranslation } from "../hooks/useMessageTranslation";
import type { Attachment } from "../types";

interface ChatContentProps {
  isFromContact: boolean;
  body: string;
  sentAsSms?: boolean;
  sentAsSmsTo?: string | null;
  sentAsSmsFrom?: string | null;
  smsFailedReason?: string | null;
  receivedViaSms?: boolean;
  attachments?: Attachment[];
  className?: string;
  translation: MessageTranslation;
  translateInBubble: boolean;
}

const smsFailedLabel = (reason: string): string =>
  reason === "no_number_configured"
    ? "Not also sent as SMS — no phone number is set up to send from"
    : "Not also sent as SMS";

const smsMirrorLabel = (to?: string | null, from?: string | null): string => {
  if (!to) return "Also sent as SMS";
  const fromSuffix = from ? ` from ${formatPhone(from)}` : "";
  return `Also sent as SMS to ${formatPhone(to)}${fromSuffix}`;
};

// Native chat messages are plain text — no from/to header or dividers.
export const ChatContent = ({
  isFromContact,
  body,
  sentAsSms,
  sentAsSmsTo,
  sentAsSmsFrom,
  smsFailedReason,
  receivedViaSms,
  attachments,
  className,
  translation,
  translateInBubble
}: ChatContentProps) => {
  const displayBody =
    translation.showingTranslation && translation.translatedText !== null
      ? translation.translatedText
      : body;

  return (
    <MessageContentCard isFromContact={isFromContact} className={className}>
      <Show if={Boolean(body)}>
        <div className="text-sm text-content-1000 [overflow-wrap:anywhere] whitespace-pre-wrap">
          <LinkifiedText text={displayBody} showFullUrl />
        </div>
      </Show>
      <MessageAttachments attachments={attachments} />
      <Show if={Boolean(receivedViaSms) && isFromContact}>
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-alpha-40 pt-3 text-xs font-medium text-content-300">
          <span>via SMS</span>
          <Show if={translateInBubble}>
            <MessageTranslateButton translation={translation} />
          </Show>
        </div>
      </Show>
      <Show if={Boolean(sentAsSms) && !isFromContact}>
        <div className="mt-3 border-t border-alpha-40 pt-3 text-xs font-medium text-content-300">
          {smsMirrorLabel(sentAsSmsTo, sentAsSmsFrom)}
        </div>
      </Show>
      <Show if={Boolean(smsFailedReason) && !sentAsSms && !isFromContact}>
        <div className="mt-3 border-t border-alpha-40 pt-3 text-xs font-medium text-warning-600">
          {smsFailedLabel(smsFailedReason ?? "")}
        </div>
      </Show>
    </MessageContentCard>
  );
};
